import { Tab, Tabs } from '@mui/material';
import fuzzy from 'fuzzy';
import { Infinity as InfinityIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import { useGroupRules, UserGroupRules, useSelectedPartner } from 'src/graphql';
import { buildPartnerUrl, getPartnerFriendlyIdentifier } from 'src/routes/hooks';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { PartnerOption, usePartnersDialogOptions } from './use-partners-dialog-options';

enum PartnersDialogTabs {
  all = 'all',
  current = 'current',
}

const tabs = [
  {
    value: PartnersDialogTabs.all,
    label: 'All',
  },
  {
    value: PartnersDialogTabs.current,
    label: 'Current',
  },
];

// The multi-tenancy option has a null partner id; rows need a string one.
const MULTI_TENANCY_ROW_ID = 'multi-tenancy';

interface PartnerRow extends ModalListItem {
  option: PartnerOption;
}

const toRowId = ({ id }: PartnerOption) => (id === null ? MULTI_TENANCY_ROW_ID : String(id));

const rankPartners = (options: PartnerOption[], search: string) =>
  fuzzy
    .filter(search, options, { extract: ({ label }) => label })
    .map(({ original }) => original);

// While searching the best match leads the list, so it is the row the list highlights;
// multi-tenancy mode only shows on top (pinnedFirst) and leads itself when it is the best match.
const searchPartners = (options: PartnerOption[], search: string) => {
  if (!search) {
    return options;
  }
  const ranked = rankPartners(options, search);
  const multiTenancy = options.find(({ id }) => id === null);
  if (!multiTenancy || ranked[0] === multiTenancy) {
    return ranked;
  }
  return [...ranked.filter((option) => option !== multiTenancy), multiTenancy];
};

export const SelectPartnerModal = () => {
  const { closeModal } = useModalMethods(Modals.SelectPartnerModal);
  const { isOpened } = useModalState(Modals.SelectPartnerModal);
  const { showModal: showCreatePartnerModal } = useModalMethods(Modals.CreatePartnerModal);
  const { checkValidity } = useGroupRules();
  const { selectedPartner } = useSelectedPartner();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(PartnersDialogTabs.current);
  const [search, setSearch] = useState('');

  const { partnersOptions } = usePartnersDialogOptions({
    currentPartner: activeTab === PartnersDialogTabs.current,
  });

  const rows = useMemo<PartnerRow[]>(
    () =>
      searchPartners(partnersOptions, search).map((option) => ({
        id: toRowId(option),
        title: option.label,
        ...(option.id === null
          ? { icon: <InfinityIcon />, pinnedFirst: true }
          : { avatar: <ColoredAvatar name={option.label} /> }),
        option,
      })),
    [partnersOptions, search],
  );

  const canCreatePartner = checkValidity([UserGroupRules.AccessToCreateNewPartner]);

  const selectPartner = ({ option }: PartnerRow) => {
    closeModal();
    if (option.id === (selectedPartner?.id ?? null)) {
      return;
    }
    // defer navigation to let it close modal first
    setTimeout(() => {
      navigate(
        buildPartnerUrl({
          existingIdFromUrl: getPartnerFriendlyIdentifier(selectedPartner),
          newPartner: option.rawPartner,
          pathname: location.pathname,
        }),
        { replace: true, flushSync: true },
      );
    });
  };

  return (
    <ModalContainer
      open={isOpened}
      onClose={closeModal}
      size="medium"
      title="Select partner"
      data-testid="select-partner-dialog"
    >
      <ModalList
        items={rows}
        onSelect={selectPartner}
        shouldFilter={false}
        onSearchChange={setSearch}
        placeholder="Select partner"
        belowFilter={
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue as PartnersDialogTabs)}
            sx={{ mt: 1.5 }}
          >
            {tabs.map((el) => (
              <Tab
                key={el.label}
                data-testid={`select-partner-tab-${el.value}`}
                value={el.value}
                label={el.label}
              />
            ))}
          </Tabs>
        }
        createAction={
          canCreatePartner
            ? {
                label: 'Create partner',
                onSelect: () => showCreatePartnerModal(null),
                pinned: true,
              }
            : undefined
        }
        testId="select-partner"
      />
    </ModalContainer>
  );
};
